import _ from 'lodash';
import { SortOrder } from 'antd/lib/table/interface';

import Api from 'models/Api';
import { ApiResponseData, ApiResponseDataWithResult } from 'models/ApiResponseData';
import { ISubscriptionApiData, ISubscriptionCreateApiData, ISubscriptionUpdateApiData } from 'models/subscription/ISubscriptionApiData';
import { ISubscriptionSearchApiData } from 'models/subscription/ISubscriptionSearchApiData';
import { SubscriptionState } from 'models/subscription/SubscriptionState';
import { IUserApiData } from 'models/user/IUserApiData';
import { SearchApi } from 'models/SearchApi';

import { UserInfo, UserInfoResult } from 'components/public/pages/subscriptions/BulkSubscribe';

export type ISubscriptionApiSearchOrdering = 'id' | 'active' | 'created_at';

export interface ISubscriptionApiSearchFilterParams {
    id?: string;
    active?: boolean;
    created_at?: string;
    order_by?: ISubscriptionApiSearchOrdering;
    order?: SortOrder;
}

export interface ISubscriptionApiSearchParams {
    subscription_plan_ids?: number[];
    page?: number;
    page_size?: number;
    filters?: ISubscriptionApiSearchFilterParams;
}

export class SubscriptionApi {
    public static async get(id: number): Promise<ISubscriptionApiData> {
        const response = await Api.get(`/api/v1/subscriptions/${id}`);
        const json = await response.json();
        return json.subscription;
    }

    public static async getAll() {
        const response = await Api.get('/api/v1/subscriptions');
        const json = await response.json();
        return json.subscriptions;
    }

    public static async hasValidSubscription(user_id: number): Promise<boolean> {
        if (user_id == null) {
            return Promise.resolve<boolean>(false);
        }
        const response = await Api.get(`/api/v1/users/${user_id}/valid_subscription`);
        const json = await response.json();
        return json.valid;
    }

    public static async subscriptionState(user_id: number): Promise<SubscriptionState | null> {
        if (user_id == null) {
            return Promise.resolve<SubscriptionState | null>(null);
        }
        const response = await Api.get(`/api/v1/users/${user_id}/subscription_state`);
        const json = await response.json();
        return json.state;
    }

    public static async userSubscriptions(user_id: number): Promise<ISubscriptionApiData[]> {
        const response = await Api.get(`/api/v1/users/${user_id}/subscriptions`);
        const json = await response.json();
        return json.subscriptions;
    }

    public static async createSubscription(body: ISubscriptionCreateApiData): Promise<any> {
        const response = await Api.post('/api/v1/subscriptions', {
            body: JSON.stringify({
                user_id: body.user_id.toString(),
                subscription_plan_id: body.subscription_plan_id.toString(),
                subscription_plan_key: body.subscription_plan_key,
                stripe_setup_intent: body.stripe_setup_intent,
                credit_card_id: body.credit_card_id ? body.credit_card_id.toString() : undefined,
                billing_address_id: body.billing_address_id ? body.billing_address_id.toString() : undefined
            })
        });
        return await response.json();
    }

    public static async getSubscribers(subscription_id: number): Promise<IUserApiData[]> {
        const response = await Api.get(`/api/v1/subscriptions/${subscription_id}/subscribers`);
        const json = await response.json();
        return json.subscribers;
    }

    public static async update(subscription_id: number, params: ISubscriptionUpdateApiData): Promise<ISubscriptionApiData> {
        const response = await Api.post(`/api/v1/subscriptions/${subscription_id}`, {
            body: JSON.stringify(params)
        });
        const json = await response.json();
        return json.subscription;
    }

    public static async cancelSubscription(subscription_id: number): Promise<ApiResponseData> {
        const response = await Api.post(`/api/v1/subscriptions/${subscription_id}/cancel`);
        return await response.json();
    }

    public static async resumeSubscription(subscription_id: number): Promise<ApiResponseData> {
        const response = await Api.post(`/api/v1/subscriptions/${subscription_id}/resume`);
        return await response.json();
    }

    public static async promoteToManager(subscription_id: number, body: { user_id: number }): Promise<ApiResponseData> {
        const response = await Api.post(`/api/v1/subscriptions/${subscription_id}/promote`, {
            body: JSON.stringify(body)
        });
        return await response.json();
    }

    public static async addSubscribersToSubscription(subscription_id: number, body: { subscriber_ids: string[] }): Promise<ApiResponseData> {
        const response = await Api.post(`/api/v1/subscriptions/${subscription_id}/add_subscribers`, {
            body: JSON.stringify(body)
        });
        return await response.json();
    }

    public static async bulkCreateSubscribers(subscription_id: number, body: { users: UserInfo[] }): Promise<ApiResponseDataWithResult<UserInfoResult>> {
        const response = await Api.post(`/api/v1/subscriptions/${subscription_id}/create_subscribers`, {
            body: JSON.stringify(body)
        });
        return await response.json();
    }

    public static async leaveSubscription(subscription_id: number): Promise<ApiResponseData> {
        const response = await Api.post(`/api/v1/subscriptions/${subscription_id}/leave`);
        return await response.json();
    }

    public static async kickSubscriber(subscription_id: number, body: { subscriber_ids: number[] }): Promise<ApiResponseData> {
        const response = await Api.post(`/api/v1/subscriptions/${subscription_id}/kick`, {
            body: JSON.stringify(body)
        });
        return await response.json();
    }

    public static async countSubscriptions(searchParams?: { subscription_plan_ids?: number[] }): Promise<number[]> {
        let query = '';
        if (searchParams) {
            query += '?';
            if (searchParams.subscription_plan_ids && searchParams.subscription_plan_ids.length > 0) {
                query += `subscription_plan_ids=${searchParams.subscription_plan_ids.join(',')}`;
            }
        }
        const response = await Api.get(`/api/v1/subscriptions/count${query}`);
        return await response.json();
    }

    public static async getSubscriptionSearch(searchParams?: ISubscriptionApiSearchParams): Promise<ISubscriptionSearchApiData> {
        let query: string[] = [];
        let queryStr = '';
        if (searchParams) {
            const { subscription_plan_ids } = searchParams;
            if (subscription_plan_ids && subscription_plan_ids.length > 0) {
                query.push(`plan_ids=${searchParams.subscription_plan_ids.join(',')}`);
            }
            queryStr = SearchApi.buildQueryFromParams(searchParams, query);
        }

        const response = await Api.get(`/api/v1/subscriptions/search${queryStr}`);
        return await response.json();
    }

    public static async getSubscriptions(subscription_ids: number[]) {
        let query: string[] = [];
        let queryStr = '';
        if (subscription_ids) {
            query.push(`ids=${subscription_ids.join(',')}`);
        }
        queryStr = `?${query.join('&')}`;
        const response = await Api.get(`/api/v1/subscriptions${queryStr}`);
        return await response.json();
    }
}
